import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { DokumenteService, FileUpload, FileUploadResponse } from '../services/dokumente.service';

@Component({
  templateUrl: './dokumente.component.html',
  styleUrls: ['./dokumente.component.scss']
})
export class DokumenteComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = ['filename', 'type_size', 'uploaded_by', 'upload_date', 'actions'];
  dataSource: MatTableDataSource<FileUpload>;
  
  private destroy$ = new Subject<void>();
  
  // Loading and pagination
  isLoadingResults = false;
  totalItems = 0;
  currentPage = 1;
  pageSize = 25;
  pageSizeOptions = [10, 25, 50, 100];
  searchValue = '';

  constructor(
    private dokumenteService: DokumenteService,
    private snackBar: MatSnackBar
  ) {
    this.dataSource = new MatTableDataSource<FileUpload>([]);
  }

  ngOnInit(): void {
    this.loadSystemDocuments();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    if (this.paginator && this.sort) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    }
  }

  loadSystemDocuments(): void {
    this.isLoadingResults = true;
    
    // Load system documents (table_name='system', record_id='global')
    // Use server-side search if searchValue is provided
    const searchTerm = this.searchValue && this.searchValue.trim().length > 0 ? this.searchValue.trim() : undefined;
    
    this.dokumenteService.getSystemDokumente(this.currentPage, this.pageSize, searchTerm).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: FileUploadResponse) => {
        const docs = Array.isArray(response.data) ? response.data : [];
        
        // Sort by filename A-Z by default
        docs.sort((a, b) => {
          const filenameA = (a.original_filename || a.filename || '').toLowerCase();
          const filenameB = (b.original_filename || b.filename || '').toLowerCase();
          return filenameA.localeCompare(filenameB, 'de', { numeric: true, sensitivity: 'base' });
        });
        
        this.dataSource.data = docs;
        this.totalItems = response.total || 0;
        this.isLoadingResults = false;
      },
      error: (error) => {
        console.error('Error loading system documents:', error);
        this.snackBar.open('Fehler beim Laden der Dokumente', 'Schließen', { duration: 3000 });
        this.isLoadingResults = false;
      }
    });
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadSystemDocuments(); // Reload with search term
  }

  clearSearch(): void {
    this.searchValue = '';
    this.currentPage = 1;
    this.loadSystemDocuments(); // Reload without search term
  }

  onPageChange(event: any): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadSystemDocuments(); // Reload with new page/size
  }

  downloadDokument(dokument: FileUpload): void {
    this.dokumenteService.downloadDokument(dokument.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = dokument.original_filename;
        link.click();
        window.URL.revokeObjectURL(url);
        this.snackBar.open('Dokument wird heruntergeladen', 'Schließen', { duration: 2000 });
      },
      error: (error) => {
        console.error('Error downloading dokument:', error);
        this.snackBar.open('Fehler beim Herunterladen des Dokuments', 'Schließen', { duration: 5000 });
      }
    });
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileTypeIcon(mimeType: string): string {
    if (!mimeType) return 'insert_drive_file';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video_file';
    if (mimeType.startsWith('audio/')) return 'audio_file';
    if (mimeType.includes('pdf')) return 'picture_as_pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'description';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('csv')) return 'table_chart';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'slideshow';
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z')) return 'folder_zip';
    if (mimeType.startsWith('text/')) return 'text_snippet';
    return 'insert_drive_file';
  }

  getDisplayCategory(mimeType: string): string {
    if (!mimeType) return 'Dokument';
    if (mimeType.startsWith('image/')) return 'Bild';
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('csv')) return 'Kalkulation';
    if (mimeType.includes('word') || mimeType.includes('document') || mimeType.startsWith('text/')) return 'Dokument';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'Präsentation';
    if (mimeType.startsWith('video/')) return 'Video';
    if (mimeType.startsWith('audio/')) return 'Audio';
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z')) return 'Archiv';
    return 'Dokument';
  }
}

